import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, X, Camera, CheckCircle } from "@phosphor-icons/react";
import { useToast } from "@/hooks/use-toast";

interface DemoImageUploadProps {
  onImagesChange: (imageUrls: string[]) => void;
  maxImages?: number;
  maxSizeMB?: number;
}

export const DemoImageUpload = ({ onImagesChange, maxImages = 5, maxSizeMB = 2 }: DemoImageUploadProps) => {
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const isImageFile = (file: File): boolean => {
    return file.type.startsWith('image/');
  };

  const validateFile = (file: File): string | null => {
    if (!isImageFile(file)) {
      return 'Doar fișierele imagine sunt permise';
    }

    if (images.length >= maxImages) {
      return `Maxim ${maxImages} imagini permise în demo`;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      return `Fișierul este prea mare. Maxim ${maxSizeMB}MB în demo`;
    }

    return null;
  };

  const handleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    setUploading(true);

    try {
      const newImages: string[] = [];

      for (const file of fileArray) {
        const validation = validateFile(file);
        if (validation) {
          toast({
            title: "Eroare fișier",
            description: validation,
            variant: "destructive",
          });
          continue;
        }

        // Create local URL for demo purposes
        const localUrl = URL.createObjectURL(file);
        newImages.push(localUrl);
      }

      if (newImages.length > 0) {
        const updatedImages = [...images, ...newImages];
        setImages(updatedImages);
        onImagesChange(updatedImages);

        toast({
          title: `${newImages.length} imagine${newImages.length > 1 ? 'i' : 'a'} încărcată cu succes!`,
          description: "În versiunea completă imaginile sunt optimizate automat în WebP",
        });
      }
    } catch (error) {
      console.error('Error uploading demo images:', error);
      toast({
        title: "Eroare la încărcare",
        description: "Încearcă din nou cu alt fișier",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index: number) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    onImagesChange(updatedImages);

    toast({
      title: "Imagine ștearsă",
      description: "Imaginea a fost eliminată din demo",
    });
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive 
            ? 'border-primary bg-primary/5' 
            : uploading 
            ? 'border-muted-foreground bg-muted/50' 
            : 'border-muted-foreground/25 hover:border-muted-foreground/50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileInput}
          className="absolute inset-0 opacity-0 cursor-pointer"
          disabled={uploading || images.length >= maxImages}
        />
        
        <div className="space-y-3">
          {uploading ? (
            <>
              <Upload className="mx-auto text-primary animate-bounce" size={32} />
              <p className="text-sm font-medium">Se încarcă imaginile demo...</p>
            </>
          ) : (
            <>
              <Camera className="mx-auto text-muted-foreground" size={32} />
              <div>
                <p className="text-sm font-medium mb-1">
                  Trage și plasează imaginile sau 
                  <Button 
                    type="button" 
                    variant="link" 
                    className="p-0 h-auto text-sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {" "}selectează fișiere
                  </Button>
                </p>
                <p className="text-xs text-muted-foreground">
                  Maxim {maxImages} imagini • {maxSizeMB}MB per imagine în demo
                </p>
              </div>
            </>
          )}
        </div>

        {images.length >= maxImages && (
          <p className="text-xs text-amber-600 mt-2">
            Ai atins limita de {maxImages} imagini pentru demo
          </p>
        )}
      </div>

      {/* Demo Info Banner */}
      {images.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="text-blue-600 mt-0.5" size={16} />
              <div className="text-sm">
                <p className="font-medium text-blue-900 mb-1">
                  🎯 Demo funcțional completat!
                </p>
                <p className="text-blue-700">
                  În versiunea completă: optimizare automată WebP, compresie 90%, 
                  încărcare pe CDN global pentru viteză maximă.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Imagini încărcate ({images.length}/{maxImages})</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {images.map((imageUrl, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square bg-muted rounded-lg overflow-hidden">
                  <img 
                    src={imageUrl} 
                    alt={`Imagine ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeImage(index)}
                >
                  <X size={12} />
                </Button>
                <div className="absolute bottom-1 left-1 bg-black/70 text-white px-1.5 py-0.5 rounded-sm text-xs">
                  {index + 1}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pro Tips */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <h4 className="text-sm font-medium mb-2">💡 Sfaturi pentru fotografii profesionale:</h4>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Prima imagine = cea mai importantă (apare în listări)</li>
            <li>• Fotografiază în lumină naturală, evită blițul</li>
            <li>• Include exterior, interior, motor, bord, defecte</li>
            <li>• Unghiuri multiple pentru fiecare zonă importantă</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};